import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CREATE_TITLE_COST, RANK_NAMES, RANK_ORDER, formatDate } from '@pk/shared';
import type { FrontierRegionView, Id, TitleSummary, TitleView } from '@pk/shared';
import { actorOf, useCommand, useFrontier, useHouse, useWorld } from '../api/hooks';
import { Badge, Btn, Field, Inline, Panel, Segmented } from '../components/ui';
import { IconBanner, IconCrown } from '../components/icons';
import { TitleLink, useEta } from '../components/game';
import '../styles/map.css';

type Mode = 'alone' | 'charter';
type T = (k: string, o?: Record<string, unknown>) => string;

const costLine = (c: { gold: number; timber: number; stone: number; manpower?: number }, t: T): string =>
  [`${c.gold} ${t('resource.gold').toLowerCase()}`, `${c.timber} ${t('resource.timber').toLowerCase()}`, `${c.stone} ${t('resource.stone').toLowerCase()}`,
    ...(c.manpower ? [`${c.manpower} ${t('resource.manpower').toLowerCase()}`] : [])].join(', ');

/** Status and the Settle action of a frontier site (shown in the map's holding panel). */
export function SettlePanel({ site }: { site: TitleSummary }) {
  const { t } = useTranslation();
  const eta = useEta();
  const house = useHouse();
  const world = useWorld();
  const fv = useFrontier();
  const cmd = useCommand();
  const f = site.frontier;
  const me = fv.data?.me;
  const day = world.data?.day ?? 0;
  const [charId, setCharId] = useState('');
  const [mode, setMode] = useState<Mode>('alone');
  const [charterId, setCharterId] = useState('');
  const [patronId, setPatronId] = useState('');

  const candidates = me?.candidates ?? [];
  const settler = candidates.find((c) => c.charId === charId) ?? candidates.find((c) => c.reason === null) ?? candidates[0];
  const settlerId = settler?.charId ?? '';
  const accepted = (me?.charters ?? []).filter((c) => c.status === 'accepted' && c.charId === settlerId);
  const pending = (me?.charters ?? []).filter((c) => c.status === 'pending' && c.charId === settlerId);
  const acceptedKey = accepted.map((c) => c.proposalId).join(',');
  useEffect(() => {
    const ids = acceptedKey ? acceptedKey.split(',') : [];
    if (mode === 'charter' && ids.length > 0 && !ids.includes(charterId)) setCharterId(ids[0]);
  }, [mode, acceptedKey, charterId]);

  if (!f) return null;
  const header = (
    <div className="small">
      {f.settled ? (
        <>
          <Badge tone="green">{t('frontier.settled')}</Badge>{' '}
          {f.protectedUntilDay !== null && f.protectedUntilDay > day && <span className="muted">{t('frontier.protectedUntil', { date: formatDate(f.protectedUntilDay) })}</span>}
          {f.charterUntilDay !== null && f.charterUntilDay >= day && f.charterPatronTitleId && (
            <div className="muted">{t('frontier.charteredUnder')} <TitleLink id={f.charterPatronTitleId} /> {t('frontier.charterUntil', { date: formatDate(f.charterUntilDay) })}</div>
          )}
        </>
      ) : f.settlingDoneDay !== null ? (
        <><Badge tone="gold">{t('frontier.settling')}</Badge> <span className="muted">{t('frontier.settlerArrives', { date: formatDate(f.settlingDoneDay), eta: eta(f.settlingDoneDay) })}</span></>
      ) : (
        <Badge tone="blue">{t('frontier.free')}</Badge>
      )}
    </div>
  );
  if (f.settled || f.settlingDoneDay !== null || (f.kind !== 'site' && f.kind !== 'city_site')) return <div className="mp-frontier">{header}</div>;

  const h = house.data;
  if (!h || !me) return <div className="mp-frontier">{header}<p className="small muted">{t('frontier.needHouse')}</p></div>;

  const kind = f.kind;
  const costs = me.costs[kind];
  const chartered = mode === 'charter';
  const reason = !settler ? t('frontier.noSettler')
    : settler.reason ?? me.reasons[kind][chartered ? 'chartered' : 'alone'] ?? (chartered && accepted.length === 0 ? t('frontier.needCharter') : null);
  const useCharter = chartered ? (accepted.some((c) => c.proposalId === charterId) ? charterId : accepted[0]?.proposalId) : undefined;

  return (
    <div className="mp-frontier">
      {header}
      <p className="small" style={{ margin: '4px 0' }}>
        <b>{t(kind === 'city_site' ? 'frontier.cityCost' : 'frontier.siteCost')}</b>: {costLine(costs.total, t)}.
        <br />
        <span className="muted">{t('frontier.charterCost', { you: costLine(costs.chartered, t), patron: costLine(costs.patron, t) })}</span>
      </p>
      <Field label={t('frontier.settler')}>
        <select className="input" value={settlerId} onChange={(e) => setCharId(e.target.value)}>
          {candidates.map((c) => <option key={c.charId} value={c.charId}>{c.name} · {t('common.age', { age: c.age })}{c.reason ? ` — ${c.reason}` : ''}</option>)}
        </select>
      </Field>
      <Segmented<Mode> value={mode} onChange={setMode} options={[{ value: 'alone', label: t('frontier.alone') }, { value: 'charter', label: t('frontier.chartered') }]} />
      {mode === 'alone'
        ? <p className="small muted" style={{ margin: '4px 0' }}>{t('frontier.aloneHelp')}</p>
        : (
          <div className="charter-box">
            <p className="small muted" style={{ margin: '4px 0' }}>{t('frontier.charterHelp')}</p>
            {accepted.length > 0 && (
              <Field label={t('frontier.charterSealed')}>
                <select className="input" value={useCharter ?? ''} onChange={(e) => setCharterId(e.target.value)}>
                  {accepted.map((c) => <option key={c.proposalId} value={c.proposalId}>{c.patronName}{c.patronTitle ? ` (${c.patronTitle})` : ''}{c.validUntilDay !== null ? ` — ${t('frontier.useBy', { date: formatDate(c.validUntilDay) })}` : ''}</option>)}
                </select>
              </Field>
            )}
            {pending.map((c) => <p key={c.proposalId} className="small muted" style={{ margin: '2px 0' }}>{t('frontier.charterPending', { name: c.patronName })}</p>)}
            {accepted.length === 0 && pending.length === 0 && settler && !settler.reason && (
              <AskPatron settlerId={settlerId} patronId={patronId} setPatronId={setPatronId} cmd={cmd} actor={actorOf(h)} />
            )}
          </div>
        )}
      {reason && <p className="small inline-error" role="status" style={{ margin: '4px 0' }}>{reason}</p>}
      <Btn variant="primary" block busy={cmd.pending} disabled={!!reason || !settlerId}
        onClick={() => void cmd.run({ type: 'settle_site', actorId: actorOf(h), charId: settlerId, siteId: site.id, ...(useCharter ? { charterId: useCharter } : {}) })}>
        {t(kind === 'city_site' ? 'frontier.foundCity' : 'frontier.settle')}
      </Btn>
      <Inline error={cmd.error} />
    </div>
  );
}

/** Patron picker + "send the charter proposal" (a letter that travels). Patrons: counts and above of other houses. */
function AskPatron({ settlerId, patronId, setPatronId, cmd, actor }: {
  settlerId: Id; patronId: Id; setPatronId: (v: Id) => void; cmd: ReturnType<typeof useCommand>; actor: Id;
}) {
  const { t } = useTranslation();
  const house = useHouse();
  const world = useWorld();
  const mine = house.data?.house.id;
  const patrons = useMemo(() => (world.data?.titles ?? [])
    .filter((x) => x.holderId && x.holderHouseId !== mine && RANK_ORDER[x.rank] >= RANK_ORDER.county && !x.frontier?.uncreated)
    .sort((a, b) => RANK_ORDER[b.rank] - RANK_ORDER[a.rank] || a.name.localeCompare(b.name)), [world.data, mine]);
  const chosen = patronId || patrons[0]?.holderId || '';
  return (
    <>
      <Field label={t('frontier.patron')}>
        <select className="input" value={chosen} onChange={(e) => setPatronId(e.target.value)}>
          {patrons.map((x) => <option key={x.id} value={x.holderId!}>{x.holderName} — {RANK_NAMES[x.rank].title} {x.name}</option>)}
        </select>
      </Field>
      <Btn small block busy={cmd.pending} disabled={!chosen || !settlerId}
        onClick={() => void cmd.run({ type: 'propose_charter', actorId: actor, charId: settlerId, patronId: chosen })}>
        {t('frontier.askCharter')}
      </Btn>
    </>
  );
}

/** "Create this title" on an uncreated frontier county / duchy / kingdom (title page). */
export function CreateTitlePanel({ title }: { title: TitleView }) {
  const { t } = useTranslation();
  const house = useHouse();
  const fv = useFrontier();
  const cmd = useCommand();
  const f = title.frontier;
  if (!f?.uncreated || (f.kind !== 'county' && f.kind !== 'duchy' && f.kind !== 'kingdom')) return null;
  const rank = f.kind;
  const def = CREATE_TITLE_COST[rank];
  const entry = fv.data?.me?.create.find((c) => c.titleId === title.id);
  const what = t(`frontier.create.needs_${rank}`, { count: def.needs });
  const reason = !house.data ? t('frontier.needHouse') : entry?.reason ?? (entry ? null : what);
  const rankName = RANK_NAMES[title.rank].title.toLowerCase();
  return (
    <Panel icon={<IconCrown />} title={t('frontier.create.title', { rank: rankName })} tone="gold">
      <p className="small" style={{ margin: '0 0 6px' }}>{t('frontier.create.help', { what, gold: def.gold, prestige: def.prestige })}</p>
      {entry && <p className="small muted" style={{ margin: '0 0 6px' }}>{t('frontier.create.have', { have: entry.have, needs: entry.needs })}</p>}
      {reason && <p className="small inline-error" role="status" style={{ margin: '0 0 6px' }}>{reason}</p>}
      <Btn variant="primary" block busy={cmd.pending} disabled={!!reason || !entry?.actorId}
        onClick={() => void cmd.run({ type: 'create_title', actorId: entry?.actorId ?? actorOf(house.data), titleId: title.id })}>
        {t('frontier.create.button', { rank: rankName, gold: def.gold, prestige: def.prestige })}
      </Btn>
      <Inline error={cmd.error} />
    </Panel>
  );
}

/** Frontier regions: open day, the war truce, how many sites are settled; for the realms sheet. */
export function FrontierRegions({ regions, day, onShow }: { regions: FrontierRegionView[]; day: number; onShow: (id: Id) => void }) {
  const { t } = useTranslation();
  if (regions.length === 0) return null;
  return (
    <section className="rp-sec rp-frontier">
      <h4>{t('frontier.regions')} <span className="count">{regions.length}</span></h4>
      <ul className="plain">
        {regions.map((r) => (
          <li key={r.id} className="rp-row">
            <button type="button" className="rp-name" onClick={() => onShow(r.sites[0]?.id ?? r.kingdomId)}>
              <span><IconBanner size={16} /> {r.name}</span>
              <small>
                {t('frontier.regionLine', { open: formatDate(r.openDay), settled: r.settledCount, total: r.sites.length })}
                {' · '}{day < r.truceEndDay ? t('frontier.truceUntil', { date: formatDate(r.truceEndDay) }) : t('frontier.truceOver')}
              </small>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
